import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { RemoveLiquidityTransaction, REMOVE_LIQUIDITY_STATUS } from '../../core/pools';
import { StrategyState } from '../../core/services/strategy';
import { lpT } from '../../core/services/token';
import { date, errorMessage, loadingBar, tokenBalance } from '../../shared/templates';
import { PoolTransactionBaseElement } from './pool-transaction-base';

const statusInvestedTemplate = function (this: RemoveLiquidityTransactionStatusElement) {

    if (!this.transaction) return;

    const { pool, preview } = this.transaction.state;

    const lpToken = lpT(pool);

    return html`
    <div class="preview">
        <div class="preview-item preview-item-big">
            <span class="label">Removing Liquidity</span>
            ${ tokenBalance(preview?.lpTokensBurned, lpToken) }
        </div>
        ${ loadingBar('Transaction pending...') }
    </div>
    `;
};

const statusDivestedTemplate = function (this: RemoveLiquidityTransactionStatusElement) {

    if (!this.transaction) return;

    const { pool, preview } = this.transaction.state;

    return html`
    <div class="preview">
        <div class="preview-item preview-item-big">
            <span class="label">Removing Liquidity</span>
            ${ tokenBalance(preview?.baseOut, pool?.token) }
        </div>
        ${ loadingBar('Transaction pending...') }
    </div>
    `;
};

const template = function (this: RemoveLiquidityTransactionStatusElement) {

    if (!this.transaction) return;

    const isPending = this.transaction?.status === REMOVE_LIQUIDITY_STATUS.PENDING || this.transaction?.status === REMOVE_LIQUIDITY_STATUS.APPROVING;
    const isFetching = this.transaction.status === REMOVE_LIQUIDITY_STATUS.UPDATING;
    const hasError = this.transaction?.status === REMOVE_LIQUIDITY_STATUS.ERROR || this.transaction?.status === REMOVE_LIQUIDITY_STATUS.FAILURE;

    if (!isFetching && !isPending && !hasError) return nothing;

    const { pool, preview, info } = this.transaction.state;

    if (!pool || !preview || !info) return nothing;

    const isDivested = info.strategyInfo.state === StrategyState.DIVESTED;

    return html`
    <div class=${ classMap({ widget: true, fetching: isFetching, pending: isPending, error: hasError }) }>

        <div class="widget-header">
            <label>
                <ill-token-symbol .name=${ pool?.token.image } .ipt=${ true }></ill-token-symbol>
                ${ date(pool?.maturity) }
            </label>
        </div>

        <div class="widget-main">

            ${ hasError
                ? errorMessage(this.transaction.error?.message ?? 'An unknown error occurred.', 'exclamation')
                : isFetching
                    ? html`
                    <div class="preview">
                        ${ loadingBar('Updating pool information...') }
                    </div>
                    `
                    : !isDivested
                        ? statusInvestedTemplate.call(this)
                        : statusDivestedTemplate.call(this)
            }

        </div>
    </div>
    `;
};

@customElement('ill-remove-liquidity-transaction-status')
export class RemoveLiquidityTransactionStatusElement extends PoolTransactionBaseElement<RemoveLiquidityTransaction> {

    protected createRenderRoot (): Element | ShadowRoot {

        return this;
    }

    protected render (): unknown {

        return template.apply(this);
    }
}
